// Receive stock's draft (docs/plans/dialog-safety.md): what was typed, kept on this device by
// `lib/drafts.ts` while the dialog is open. Product ids and typed values only; the dialog reads
// the products again when the draft is continued. Pure, unit-tested.
import {
  billDiscountPaisa,
  lineExtraPaisa,
  parseSerials,
  receiptLineAmount,
  type PercentOrAmount,
  type UnitChoice,
} from '@ims/core'

export interface ReceiveDraftLine {
  productId: string
  qtyMilli: number | null
  costPaisa: number | null
  batchNo: string
  mfgDate: string
  expiryDate: string
  expiryAuto: boolean
  mrpPaisa: number | null
  serialsText: string
  /** The purchase order line it fills. */
  orderLineId: string | null
  /** Came free, beside what was paid for (docs/plans/free-goods.md). */
  freeMilli: number | null
  /** Null while the box is closed. */
  charge: PercentOrAmount | null
  discount: PercentOrAmount | null
  /** Typed in this unit, for a product in two units. */
  enteredChoice: UnitChoice | null
}

/** The supplier's bill as typed (`BillDraft` in screens/supplier-bills.tsx). */
export interface ReceiveDraftBill {
  billDate: string
  dueDate: string
  totalPaisa: number | null
  paidPaisa: number | null
  paidFull?: boolean
  method: string
}

export interface ReceiveDraft {
  supplierId: string
  locationId: string | null
  reference: string
  bill: ReceiveDraftBill
  billTyped: boolean
  /** Off the whole bill; null while the box is closed. */
  billDiscount: PercentOrAmount | null
  lines: ReceiveDraftLine[]
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const str = (v: unknown) => (typeof v === 'string' ? v : '')
const strOrNull = (v: unknown) => (typeof v === 'string' && v ? v : null)
const int = (v: unknown) => (typeof v === 'number' && Number.isSafeInteger(v) ? v : null)

function parseBox(v: unknown): PercentOrAmount | null {
  const b = obj(v)
  if (!b || (b.mode !== 'percent' && b.mode !== 'amount')) return null
  return { mode: b.mode, bp: int(b.bp), paisa: int(b.paisa) ?? 0 }
}

function parseLine(v: unknown): ReceiveDraftLine | null {
  const l = obj(v)
  if (!l || typeof l.productId !== 'string' || !l.productId) return null
  return {
    productId: l.productId,
    qtyMilli: int(l.qtyMilli),
    costPaisa: int(l.costPaisa),
    batchNo: str(l.batchNo),
    mfgDate: str(l.mfgDate),
    expiryDate: str(l.expiryDate),
    expiryAuto: l.expiryAuto === true,
    mrpPaisa: int(l.mrpPaisa),
    serialsText: str(l.serialsText),
    orderLineId: strOrNull(l.orderLineId),
    freeMilli: int(l.freeMilli),
    charge: parseBox(l.charge),
    discount: parseBox(l.discount),
    enteredChoice:
      l.enteredChoice === 'larger' || l.enteredChoice === 'smaller' ? l.enteredChoice : null,
  }
}

/** The draft as read from storage, or null when it is not one. */
export function parseReceiveDraft(v: unknown): ReceiveDraft | null {
  const d = obj(v)
  const bill = obj(d?.bill)
  if (!d || !bill || !Array.isArray(d.lines)) return null
  const lines = d.lines.map(parseLine).filter((l): l is ReceiveDraftLine => l !== null)
  return {
    supplierId: str(d.supplierId),
    locationId: strOrNull(d.locationId),
    reference: str(d.reference),
    bill: {
      billDate: str(bill.billDate),
      dueDate: str(bill.dueDate),
      totalPaisa: int(bill.totalPaisa),
      paidPaisa: int(bill.paidPaisa),
      ...(bill.paidFull === true && { paidFull: true }),
      method: str(bill.method) || 'cash',
    },
    billTyped: d.billTyped === true,
    billDiscount: parseBox(d.billDiscount),
    lines,
  }
}

/** Worth keeping: a line, or a bill number typed. */
export function draftHasWork(d: ReceiveDraft): boolean {
  return d.lines.length > 0 || d.reference.trim() !== ''
}

const lineQty = (l: ReceiveDraftLine) =>
  l.serialsText.trim() ? parseSerials(l.serialsText).serials.length * 1000 : (l.qtyMilli ?? 0)

/** One line's amount: what was paid for, less its discount, plus the charge on free goods. */
export function lineAmount(l: {
  qty: number
  freeMilli: number | null
  costPaisa: number | null
  charge: PercentOrAmount | null
  discount: PercentOrAmount | null
}): number {
  const ratePaisa = l.costPaisa ?? 0
  return receiptLineAmount({
    qtyMilli: l.qty,
    ratePaisa,
    discountPaisa: lineExtraPaisa(l.qty, ratePaisa, l.discount),
    freeChargePaisa: lineExtraPaisa(l.freeMilli ?? 0, ratePaisa, l.charge),
  })
}

/** What the lines cost, as the dialog's footer adds it up. */
export function draftTotal(d: ReceiveDraft): number {
  const lines = d.lines.reduce((a, l) => a + lineAmount({ ...l, qty: lineQty(l) }), 0)
  return lines - billDiscountPaisa(lines, d.billDiscount)
}

/**
 * The draft's lines with their products as they are now. A product that is gone or no longer
 * active is left out (the caller passes only those), and so is a purchase order line no longer
 * due (`due`, when receiving against an order).
 */
export function restoreLines<P extends { id: string }>(
  d: ReceiveDraft,
  products: ReadonlyMap<string, P>,
  due: ReadonlySet<string> | null,
): { lines: (ReceiveDraftLine & { product: P })[]; dropped: number } {
  const lines: (ReceiveDraftLine & { product: P })[] = []
  for (const l of d.lines) {
    const product = products.get(l.productId)
    if (!product) continue
    // Something that came without being on the order has no order line: it stays.
    if (due && l.orderLineId && !due.has(l.orderLineId)) continue
    lines.push({ ...l, product })
  }
  return { lines, dropped: d.lines.length - lines.length }
}
